import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, Zap, Filter, BookOpen, MessageCircle, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';

// ── Plan limits ─────────────────────────────────────────────────────────────

export const PLAN_LIMITS: Record<string, {
  rules: number;
  fences: number;
  briefings: number;   // per month
  chatQueries: number; // per day
  postsPerDay: number;
  xAccounts: number;
}> = {
  free:            { rules: 5,         fences: 5,         briefings: 0,  chatQueries: 0,  postsPerDay: 200,   xAccounts: 0   },
  personal:        { rules: 20,        fences: 20,        briefings: 8,  chatQueries: 10, postsPerDay: 1000,  xAccounts: 50  },
  personal_annual: { rules: 20,        fences: 20,        briefings: 8,  chatQueries: 10, postsPerDay: 1000,  xAccounts: 50  },
  creator:         { rules: 999999,    fences: 999999,    briefings: 30, chatQueries: 50, postsPerDay: 5000,  xAccounts: 200 },
  creator_annual:  { rules: 999999,    fences: 999999,    briefings: 30, chatQueries: 50, postsPerDay: 5000,  xAccounts: 200 },
  family:          { rules: 999999,    fences: 999999,    briefings: 30, chatQueries: 50, postsPerDay: 3000,  xAccounts: 200 },
  family_annual:   { rules: 999999,    fences: 999999,    briefings: 30, chatQueries: 50, postsPerDay: 3000,  xAccounts: 200 },
  founding:        { rules: 999999,    fences: 999999,    briefings: 30, chatQueries: 50, postsPerDay: 5000,  xAccounts: 200 },
};

const UNLIMITED = 999999;

interface UsageMeter {
  label: string;
  icon: React.ReactNode;
  used: number;
  limit: number;
  unit?: string;
}

function UsageRow({ meter }: { meter: UsageMeter }) {
  if (meter.limit >= UNLIMITED) {
    return (
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-2 text-muted-foreground">
          {meter.icon}
          {meter.label}
        </span>
        <span className="text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
          Unlimited
        </span>
      </div>
    );
  }

  const pct = meter.limit > 0 ? Math.min((meter.used / meter.limit) * 100, 100) : 0;
  const isWarning = pct >= 80 && pct < 100;
  const isFull = pct >= 100;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-2 text-muted-foreground">
          {meter.icon}
          {meter.label}
        </span>
        <span className={`text-xs font-medium ${isFull ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-foreground'}`}>
          {meter.used} / {meter.limit}{meter.unit ? ` ${meter.unit}` : ''}
          {isFull && ' — limit reached'}
          {isWarning && ' — almost full'}
        </span>
      </div>
      <Progress
        value={pct}
        className={`h-1.5 ${isFull ? '[&>div]:bg-red-500' : isWarning ? '[&>div]:bg-amber-400' : '[&>div]:bg-[#1A2E4A]'}`}
      />
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

interface Props {
  currentTier: string | null;
}

export function PlanUsageCard({ currentTier }: Props) {
  const { user } = useAuth();
  const tier = currentTier ?? 'free';
  const limits = PLAN_LIMITS[tier] ?? PLAN_LIMITS.free;

  const [rulesCount, setRulesCount] = useState(0);
  const [fencesCount, setFencesCount] = useState(0);
  const [briefingsThisMonth, setBriefingsThisMonth] = useState(0);
  const [chatToday, setChatToday] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;

    const fetchUsage = async () => {
      setLoading(true);

      // Rules + fences from filtering_rules
      const { data: rules } = await supabase
        .from('filtering_rules')
        .select('rule_type')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (rules) {
        setRulesCount(rules.filter(r => r.rule_type !== 'filter').length);
        setFencesCount(rules.filter(r => r.rule_type === 'filter').length);
      }

      // AI usage from beta_user_config / daily_ai_usage
      const today = new Date().toISOString().split('T')[0];
      const monthStart = new Date();
      monthStart.setDate(1);
      const monthStartStr = monthStart.toISOString().split('T')[0];

      const { data: monthlyUsage } = await supabase
        .from('daily_ai_usage')
        .select('operations_by_type')
        .eq('user_id', user.id)
        .gte('usage_date', monthStartStr);

      if (monthlyUsage) {
        let briefings = 0;
        monthlyUsage.forEach((row: any) => {
          const ops = row.operations_by_type ?? {};
          briefings += (ops['weekly_briefing'] ?? 0);
        });
        setBriefingsThisMonth(briefings);
      }

      const { data: todayUsage } = await supabase
        .from('daily_ai_usage')
        .select('operations_by_type')
        .eq('user_id', user.id)
        .eq('usage_date', today)
        .maybeSingle();

      if (todayUsage) {
        const ops = (todayUsage as any).operations_by_type ?? {};
        setChatToday(ops['chat_with_feed'] ?? 0);
      }

      setLoading(false);
    };

    fetchUsage();
  }, [user?.id]);

  // Work out if any meter is at 80%+ (for upgrade nudge)
  const meters: UsageMeter[] = [
    { label: 'Listening Rules', icon: <BookOpen className="h-3.5 w-3.5" />, used: rulesCount, limit: limits.rules },
    { label: 'Fences', icon: <Filter className="h-3.5 w-3.5" />, used: fencesCount, limit: limits.fences },
    { label: 'AI Briefings this month', icon: <Sparkles className="h-3.5 w-3.5" />, used: briefingsThisMonth, limit: limits.briefings },
    { label: 'Feed Chat queries today', icon: <MessageCircle className="h-3.5 w-3.5" />, used: chatToday, limit: limits.chatQueries },
  ];

  const nearLimit = meters.some(m => m.limit < UNLIMITED && m.limit > 0 && (m.used / m.limit) >= 0.8);
  const atLimit   = meters.some(m => m.limit < UNLIMITED && m.limit > 0 && m.used >= m.limit);

  const nextTier = tier === 'free' ? 'Personal'
    : tier === 'personal' || tier === 'personal_annual' ? 'Creator'
    : null;

  if (loading) {
    return (
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-sm font-medium"><Zap className="h-4 w-4" />Plan Usage</CardTitle></CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            {[1,2,3,4].map(i => <div key={i} className="h-4 bg-muted rounded w-full" />)}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={atLimit ? 'border-red-200' : nearLimit ? 'border-amber-200' : ''}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <Zap className="h-4 w-4" />
            Plan Usage
          </CardTitle>
          {atLimit ? (
            <Badge className="bg-red-100 text-red-700 border-red-200 text-xs gap-1">
              <AlertTriangle className="h-3 w-3" /> Limit reached
            </Badge>
          ) : nearLimit ? (
            <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs gap-1">
              <AlertTriangle className="h-3 w-3" /> Almost full
            </Badge>
          ) : (
            <Badge className="bg-green-100 text-green-700 border-green-200 text-xs gap-1">
              <CheckCircle className="h-3 w-3" /> Good headroom
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground">Resets monthly · Briefings &amp; chat reset daily/monthly</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {meters.map(m => <UsageRow key={m.label} meter={m} />)}

        {(nearLimit || atLimit) && nextTier && (
          <div className={`rounded-lg p-3 text-sm flex items-start gap-3 mt-2 ${atLimit ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'}`}>
            <AlertTriangle className={`h-4 w-4 shrink-0 mt-0.5 ${atLimit ? 'text-red-500' : 'text-amber-500'}`} />
            <div className="flex-1">
              <p className={`font-medium ${atLimit ? 'text-red-800' : 'text-amber-800'}`}>
                {atLimit ? "You've hit your plan limit" : "You're approaching your plan limit"}
              </p>
              <p className={`text-xs mt-0.5 ${atLimit ? 'text-red-600' : 'text-amber-600'}`}>
                Upgrade to {nextTier} for {nextTier === 'Creator' ? 'unlimited rules, fences, and 5× more AI features' : 'more platforms and higher limits'}.
              </p>
              <Button size="sm" className="mt-2 bg-[#1A2E4A] hover:bg-[#1A2E4A]/90 text-white h-7 text-xs" asChild>
                <Link to="/dashboard/upgrade">Upgrade to {nextTier}</Link>
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
